/**
 * MODULE: _shared/frontend-shell/src/features/crm/utils/activityCoverage.ts
 * PURPOSE: Parse and guard activity coverage for deal risk/stuck UI (Sprint 0a)
 * DEPENDENCIES: None
 * PUBLIC_API: ActivityCoverage, parseActivityCoverage, pickActivityCoverageFromRow, applyCoverageGuard
 * SIDE_EFFECTS: None
 * AGENT_SAFETY: SAFE
 */

export type ActivityCoverage = 'complete' | 'partial' | 'unknown';

export function parseActivityCoverage(value: unknown): ActivityCoverage {
  if (value === 'complete' || value === 'partial' || value === 'unknown') {
    return value;
  }
  return 'unknown';
}

export function pickActivityCoverageFromRow(row: Record<string, unknown>): ActivityCoverage {
  return parseActivityCoverage(row.activity_coverage ?? row.activityCoverage);
}

function pickBool(row: Record<string, unknown>, ...keys: string[]): boolean | undefined {
  for (const key of keys) {
    const v = row[key];
    if (typeof v === 'boolean') return v;
  }
  return undefined;
}

/** Suppress at-risk/stuck flags unless coverage is complete (UAT ghost-deal guard). */
export function applyCoverageGuard(
  coverage: ActivityCoverage,
  isAtRisk?: boolean,
  isStuck?: boolean
): { isAtRisk: boolean; isStuck: boolean } {
  if (coverage !== 'complete') {
    return { isAtRisk: false, isStuck: false };
  }
  return { isAtRisk: Boolean(isAtRisk), isStuck: Boolean(isStuck) };
}

export function coverageFromOpportunityLike(opp: {
  activityCoverage?: ActivityCoverage;
  activity_coverage?: string;
  metadata?: Record<string, unknown>;
}): ActivityCoverage {
  return parseActivityCoverage(
    opp.activityCoverage ?? opp.activity_coverage ?? opp.metadata?.activity_coverage
  );
}

/** Count at-risk/stuck/no-data among open pipeline opportunities (same guard as ActivityCoverageBadge). */
export function countActivityRiskKpis(
  opportunities: Array<{
    stage?: string;
    activityCoverage?: ActivityCoverage;
    isAtRisk?: boolean;
    isStuck?: boolean;
  }>
): { dealsAtRisk: number; dealsStuck: number; dealsNoActivityData: number } {
  let dealsAtRisk = 0;
  let dealsStuck = 0;
  let dealsNoActivityData = 0;
  for (const opp of opportunities) {
    const stage = (opp.stage ?? '').toLowerCase();
    if (stage === 'closed_won' || stage === 'closed_lost') continue;
    const coverage = coverageFromOpportunityLike(opp);
    if (coverage === 'unknown') {
      dealsNoActivityData += 1;
    }
    const { isAtRisk, isStuck } = applyCoverageGuard(coverage, opp.isAtRisk, opp.isStuck);
    if (isAtRisk) dealsAtRisk += 1;
    if (isStuck) dealsStuck += 1;
  }
  return { dealsAtRisk, dealsStuck, dealsNoActivityData };
}

export function riskFlagsFromCrmRow(row: Record<string, unknown>): {
  activityCoverage: ActivityCoverage;
  isAtRisk: boolean;
  isStuck: boolean;
} {
  const activityCoverage = pickActivityCoverageFromRow(row);
  const guarded = applyCoverageGuard(
    activityCoverage,
    pickBool(row, 'is_at_risk', 'isAtRisk'),
    pickBool(row, 'is_stuck', 'isStuck')
  );
  return { activityCoverage, ...guarded };
}
